import { catalogKeySet, isKnownPermissionKey } from '../permission-catalog/permission-catalog';
import { MANAGE_ACTION, type PermissionKey } from '../permission-key/permission-key';

/**
 * Everything the evaluator needs about an actor. Assembled once at login and
 * carried in the access token, so authorization costs no database round-trip
 * (revocation is handled separately by `tokenVersion`).
 */
export interface PermissionContext {
  /** Union of the actor's role permissions and their direct grants. */
  granted: readonly PermissionKey[];
  /** Direct denies — subtracted from the grants, and they win (N22). */
  denied?: readonly PermissionKey[];
  /** `super_admin` bypasses evaluation entirely, denies included. */
  isSuperAdmin?: boolean;
  /**
   * The keys that exist. Defaults to the code-declared catalog; a phase that
   * derives keys at runtime (P4's `integrations.<capability>.manage`) passes a
   * widened set rather than introducing a second evaluator.
   */
  known?: ReadonlySet<string>;
}

const MANAGE_SUFFIX = `.${MANAGE_ACTION}`;

/**
 * Whether a **held** key authorizes a **required** key.
 *
 * - the global `manage` covers everything;
 * - `<namespace>.manage` covers every key inside that namespace, at any depth
 *   (`integrations.manage` ⊃ `integrations.ai-text.manage`);
 * - otherwise the keys must be equal.
 */
export function covers(held: PermissionKey, required: PermissionKey): boolean {
  if (held === MANAGE_ACTION) {
    return true;
  }

  if (held === required) {
    return true;
  }

  if (held.endsWith(MANAGE_SUFFIX)) {
    const namespace = held.slice(0, -MANAGE_SUFFIX.length);
    return required.startsWith(`${namespace}.`);
  }

  return false;
}

/**
 * The **one** permission evaluator (N22). `PermissionsGuard` on the api and
 * `PermissionGate` / `useHasPermission` in the admin portal both call this —
 * there is never a second implementation and never an ad-hoc
 * `permissions.includes(...)`, because the two would drift and the drift would
 * be a security hole.
 *
 * Order of decisions, all fail-closed:
 *
 * 1. `super_admin` → allow (a deny must never be able to brick the platform's
 *    last way back in — the lockout guards in `modules/access` depend on this);
 * 2. an unknown/unseeded required key → **deny**, so a typo in a decorator
 *    locks a door rather than opening one;
 * 3. a matching **deny** → deny. Denies match in **both directions**: denying
 *    `verification.review` also blocks a required `verification.manage`,
 *    because holding the wildcard would otherwise hand back the very action
 *    that was carved out. "Deny wins at any wildcard level" is read as
 *    *any overlap denies*;
 * 4. a matching grant → allow; otherwise deny.
 */
export function hasPermission(context: PermissionContext, required: PermissionKey): boolean {
  if (context.isSuperAdmin === true) {
    return true;
  }

  const known = context.known ?? catalogKeySet();
  if (!isKnownPermissionKey(required, known)) {
    return false;
  }

  const denied = context.denied ?? [];
  for (const key of denied) {
    if (covers(key, required) || covers(required, key)) {
      return false;
    }
  }

  return context.granted.some((key) => covers(key, required));
}

/** Every required key must be satisfied — the default for `@RequirePermissions`. */
export function hasAllPermissions(
  context: PermissionContext,
  required: readonly PermissionKey[],
): boolean {
  return required.every((key) => hasPermission(context, key));
}

/** At least one required key must be satisfied. */
export function hasAnyPermission(
  context: PermissionContext,
  required: readonly PermissionKey[],
): boolean {
  return required.some((key) => hasPermission(context, key));
}

/**
 * The actor's grants narrowed to the keys that actually resolve to allow — the
 * **effective-permissions preview** the admin sees before saving (P10), and the
 * shape the access module returns from its preview endpoint.
 */
export function effectivePermissions(
  context: PermissionContext,
  known: ReadonlySet<string> = context.known ?? catalogKeySet(),
): PermissionKey[] {
  if (context.isSuperAdmin === true) {
    return [...known].sort();
  }

  return [...known].filter((key) => hasPermission({ ...context, known }, key)).sort();
}
